import React from "react";
import { Controller } from "react-hook-form";
import FormLabel from "../../../shared/components/ui/FormLabel";
import InputError from "../../../shared/components/ui/InputError";
import Checkbox from "../../../shared/components/ui/Checkbox";
import { Box } from "../../../shared/components/ui/chakra";
import { Options } from "../../../shared/lib/types";
import {
  addBuildingType,
  addStructureType,
  buildingTypeIds,
  isBuildingTypeSelected,
  removeBuildingType,
  removeStructureType,
} from "../lib/buildingTypeStructureTypeHelper";
import { useStep2Form } from "../lib/useStep2Form";
import { refForCenterOfCheckboxes } from "../../../../shared/lib/refForCenterOfCheckboxes";

import { CheckboxGroup, Stack } from "@chakra-ui/react";

const BuildingTypeStructureTypeFieldForNew = ({
  methods,
  buildingTypes,
  structureTypes,
}: {
  methods: ReturnType<typeof useStep2Form>;
  buildingTypes: Options;
  structureTypes: Options;
}) => {
  const { control } = methods;

  return (
    <Box as="fieldset">
      <Box as="legend">
        <FormLabel required>対応できる建物種別</FormLabel>
      </Box>
      <Box mt={2}>
        <Controller
          control={control}
          name={`contractor_info_attributes.contractor_info_supported_building_types_attributes`}
          render={({
            field: { ref, onChange, ...field },
            fieldState: { error },
          }) => {
            const value = field.value as Array<{
              building_type_id: string;
              structure_type_ids: string[];
            }>;
            return (
              <Box>
                <CheckboxGroup {...field} value={buildingTypeIds(value)}>
                  {/*
                    column-reverseにしてreverse()をするので、結局順番は変わらない
                    エラー時のフォーカスの順番は、refを後に指定したほうにフォーカスするが、先頭に遷移したいための処理
                    A_LOOP-756 対応できる建物種別の入力エラーの際の遷移先
                  */}
                  <Stack direction="column-reverse">
                    {[...buildingTypes].reverse().map((buildingType) => (
                      <Box key={buildingType.id}>
                        <Checkbox
                          key={buildingType.id}
                          value={buildingType.id.toString()}
                          ref={error?.message != null ? ref : null}
                          // building_types_attributes自体のエラーではmessageが入ってくる
                          // building_types_attributes内の、structure_type_idsのエラーでは配列が返ってくる
                          // なのでmessageがあることをチェックし、構造種別のほうではerror自体があるかをチェックする
                          isInvalid={!!error?.message}
                          onChange={(e) => {
                            if (e.target.checked) {
                              onChange(
                                addBuildingType(
                                  value,
                                  buildingType.id.toString(),
                                ),
                              );
                            } else {
                              onChange(
                                removeBuildingType(
                                  value,
                                  buildingType.id.toString(),
                                ),
                              );
                            }
                          }}
                        >
                          {buildingType.name}
                        </Checkbox>
                        {isBuildingTypeSelected(
                          value,
                          buildingType.id.toString(),
                        ) && (
                          <Box pt={2} pl={7} position="relative">
                            <Box
                              w={0}
                              h={0}
                              borderBottom="8px var(--chakra-colors-brand-50) solid"
                              borderX="6px transparent solid"
                              position="absolute"
                              top={0}
                              ml={4}
                            />
                            <Box
                              py={4}
                              px={5}
                              bgColor="brand.50"
                              borderRadius={2}
                            >
                              <Box as="fieldset">
                                <Box as="legend">
                                  <FormLabel required>
                                    対応できる構造種別 - {buildingType.name}
                                  </FormLabel>
                                </Box>
                                <Box mt={2}>
                                  <Stack>
                                    {structureTypes.map(
                                      (structureType, index) => (
                                        <Box key={structureType.id}>
                                          <Checkbox
                                            ref={refForCenterOfCheckboxes({
                                              length: structureTypes.length,
                                              index,
                                              isInvalid:
                                                error != null &&
                                                structureTypeIdsByBuildingTypeId(
                                                  {
                                                    id: buildingType.id.toString(),
                                                    value: field.value,
                                                  },
                                                ).length === 0,
                                              ref,
                                            })}
                                            isChecked={structureTypeIdsByBuildingTypeId(
                                              {
                                                id: buildingType.id.toString(),
                                                value: field.value,
                                              },
                                            ).includes(
                                              structureType.id.toString(),
                                            )}
                                            isInvalid={
                                              !!error &&
                                              structureTypeIdsByBuildingTypeId({
                                                id: buildingType.id.toString(),
                                                value: field.value,
                                              }).length === 0
                                            }
                                            onChange={(e) => {
                                              if (e.target.checked) {
                                                onChange(
                                                  addStructureType(
                                                    value,
                                                    buildingType.id.toString(),
                                                    structureType.id.toString(),
                                                  ),
                                                );
                                              } else {
                                                onChange(
                                                  removeStructureType(
                                                    value,
                                                    buildingType.id.toString(),
                                                    structureType.id.toString(),
                                                  ),
                                                );
                                              }
                                            }}
                                          >
                                            {structureType.name}
                                          </Checkbox>
                                        </Box>
                                      ),
                                    )}
                                  </Stack>
                                </Box>
                              </Box>
                            </Box>
                          </Box>
                        )}
                      </Box>
                    ))}
                  </Stack>
                </CheckboxGroup>
                {error && <InputError>{error.message}</InputError>}
              </Box>
            );
          }}
        />
      </Box>
    </Box>
  );
};

const structureTypeIdsByBuildingTypeId = ({
  id,
  value,
}: {
  id: string;
  value: Array<{ building_type_id: string; structure_type_ids: string[] }>;
}) => {
  return value.find((v) => v.building_type_id === id)!.structure_type_ids;
};

export default BuildingTypeStructureTypeFieldForNew;
